import { useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Link as MuiLink,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';

import { adminApi, type ListParams } from '../../services/adminApi';

const STATUSES: Array<ListParams['status']> = ['pending', 'in_review', 'resolved', 'dismissed'];
const PAGE_SIZE = 25;

/**
 * Admin review queue index page.
 *
 * Shows a paginated table of review_queue_items with a status filter.
 * Each row links to the detail page for triage.
 */
export function ReviewQueuePage() {
  const [status, setStatus] = useState<ListParams['status']>('pending');
  const [page, setPage] = useState(1);

  const query = useQuery({
    queryKey: ['admin', 'review-queue', { status, page }],
    queryFn: () => adminApi.listReviewQueue({ status, page, limit: PAGE_SIZE }),
  });

  function onStatusChange(event: SelectChangeEvent<string>) {
    setStatus(event.target.value as ListParams['status']);
    setPage(1);
  }

  return (
    <Box>
      <Typography variant="h1" sx={{ fontSize: '1.6rem', mb: 2 }}>
        Review queue
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }} elevation={0} variant="outlined">
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="review-queue-status-label">Status</InputLabel>
          <Select
            labelId="review-queue-status-label"
            id="review-queue-status"
            value={status ?? 'pending'}
            label="Status"
            onChange={onStatusChange}
          >
            {STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {query.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label="Loading review queue" />
        </Box>
      )}

      {query.isError && (
        <Alert severity="error" role="alert">
          Failed to load review queue: {(query.error as Error).message}
        </Alert>
      )}

      {query.isSuccess && (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table aria-label="Review queue items">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Confidence</TableCell>
                  <TableCell>Raw title</TableCell>
                  <TableCell>Retailer</TableCell>
                  <TableCell>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {query.data.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        No items match the current filter.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  query.data.items.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <MuiLink
                          component={RouterLink}
                          to={`/admin/review-queue/${item.id}`}
                          aria-label={`Open review queue item ${item.id}`}
                        >
                          {item.id.slice(0, 8)}
                        </MuiLink>
                      </TableCell>
                      <TableCell>{item.reason}</TableCell>
                      <TableCell>
                        {item.confidenceScore == null
                          ? '—'
                          : Number(item.confidenceScore).toFixed(2)}
                      </TableCell>
                      <TableCell>{getRawTitle(item)}</TableCell>
                      <TableCell>{getRetailer(item)}</TableCell>
                      <TableCell>{item.createdAt ?? '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={Math.max(1, Math.ceil(query.data.pagination.total / PAGE_SIZE))}
              page={page}
              onChange={(_e, p) => setPage(p)}
              aria-label="Review queue pagination"
            />
          </Box>
        </>
      )}
    </Box>
  );
}

function getRawTitle(item: { rawListingId: string | null }): string {
  // The list endpoint returns only the queue item itself (no JOIN). The raw
  // title is shown on the detail page. We surface the linked raw_listing_id
  // here so reviewers know whether an item has a raw listing attached.
  return item.rawListingId ? '(see detail)' : '—';
}

function getRetailer(_item: unknown): string {
  return '—';
}

export default ReviewQueuePage;
